"use client";

import { Coins, Eye, Swords } from "lucide-react";
import { useTranslations } from "next-intl";

import {
  championLabel,
  useMatchTeamStats,
  type ReplayTeamMetrics,
  type ReplayTeamPlayer,
} from "@/entities/replay-metrics";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import { useDelayedLoading } from "@/shared/lib/hooks/use-delayed-loading";
import { getObjectiveImageUrl } from "@/shared/lib/objective-image";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { translateUi } from "@/shared/lib/ui-translate";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/navigation/accordion";

import { TowerIcon } from "./tower-icon";
import { formatNumberUi } from "@/shared/lib/ui-locale";

/**
 * KAN-793: разбор игры табло, а не сводкой команд.
 *
 * Первая версия (KAN-792) показывала только командные суммы — золото, урон, крипы,
 * обзор одной строкой на сторону. Это оказалось бесполезно: перечисленное —
 * персональные показатели, и игрок ищет в разборе СВОЮ строку, а сумма пятерых её
 * прячет. Поэтому здесь табло как после игры: строка на игрока с чемпионом, KDA,
 * крипами, золотом, уроном и обзором.
 *
 * Командными остались только те величины, которые действительно принадлежат команде:
 * башни и объекты. Они вынесены в полосу над составами.
 *
 * KAN-795: стороны НЕ красятся в синий и красный. Цвет в табло несёт смысл только
 * там, где он про величину: золото (янтарный) и полоска урона. Раскрашенные
 * названия команд конкурировали с ними за внимание и делали таблицу пёстрой;
 * какая сторона где — и так видно по расположению колонок.
 *
 * Серия (BO3/BO5) — список игр: раскрывается по одной, первая открыта сразу.
 * Матч без реплеев блока не получает вовсе: пустая таблица читалась бы как
 * «в игре ничего не происходило», а правда в том, что данных просто нет.
 */

interface MatchGameTeamStatsProps {
  matchId: string;
}

type GamePair = { blue?: ReplayTeamMetrics; red?: ReplayTeamMetrics };

// Рисуется только на клиенте (данные из запроса) — локаль берётся из <html lang>.
const int = (v: number) => formatNumberUi(Math.round(v));
const short = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : int(v));

/** Короткая подпись роли. Неизвестную роль не подписываем — пустое место честнее выдумки. */
const roleLabel = (position: string | null): string => {
  switch (position?.toUpperCase()) {
    case "TOP": return translateUi("litMatchGameTeamStats.top");
    case "JUNGLE": return translateUi("litMatchGameTeamStats.les");
    case "MIDDLE":
    case "MID": return translateUi("litMatchGameTeamStats.mid");
    case "BOTTOM":
    case "BOT":
    case "ADC": return translateUi("litMatchGameTeamStats.bot");
    case "UTILITY":
    case "SUPPORT": return translateUi("litMatchGameTeamStats.sap");
    default: return "";
  }
};

const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;

/** Объект команды: иконка и два числа. Нет данных у обеих сторон — не показываем. */
function ObjectiveStat({
  icon,
  label,
  blue,
  red,
}: {
  icon: React.ReactNode;
  label: string;
  blue: number | null | undefined;
  red: number | null | undefined;
}) {
  if (blue == null && red == null) return null;

  return (
    <div className="flex items-center gap-1.5" title={label}>
      <span className="tabular-nums text-foreground">{blue == null ? "—" : int(blue)}</span>
      {icon}
      <span className="tabular-nums text-foreground">{red == null ? "—" : int(red)}</span>
    </div>
  );
}

/** Полоса объектов: то немногое, что действительно принадлежит команде, а не игроку. */
function ObjectivesStrip({ blue, red }: GamePair) {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  const objectiveIcon = (type: string, alt: string) => (
    <img src={getObjectiveImageUrl(type)} alt={alt} className="h-4 w-4 shrink-0 object-contain" />
  );

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 border-b border-border pb-2 text-sm font-medium">
      <ObjectiveStat
        icon={
          <span className="flex items-center gap-0.5">
            <TowerIcon color="blue" className="h-3.5 w-4" />
          </span>
        }
        label={t("towers")}
        blue={blue?.totals.turrets}
        red={red?.totals.turrets}
      />
      <ObjectiveStat
        icon={objectiveIcon("dragon", t("dragons"))}
        label={t("dragons")}
        blue={blue?.objectives.dragons}
        red={red?.objectives.dragons}
      />
      <ObjectiveStat
        icon={objectiveIcon("baron", t("barons"))}
        label={t("barons")}
        blue={blue?.objectives.barons}
        red={red?.objectives.barons}
      />
      <ObjectiveStat
        icon={objectiveIcon("herald", t("heralds"))}
        label={t("heralds")}
        blue={blue?.objectives.heralds}
        red={red?.objectives.heralds}
      />
      <ObjectiveStat
        icon={objectiveIcon("grub", t("voidgrubs"))}
        label={t("voidgrubs")}
        blue={blue?.objectives.voidgrubs}
        red={red?.objectives.voidgrubs}
      />
    </div>
  );
}

/**
 * Строка игрока — как на табло после игры. Урон дополнительно показан полоской:
 * доля от лучшего в матче читается быстрее, чем сравнение пятизначных чисел.
 */
function PlayerRow({
  player,
  side,
  maxDamage,
}: {
  player: ReplayTeamPlayer;
  side: "blue" | "red";
  maxDamage: number;
}) {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  const name = player.displayName ?? player.riotIdGameName ?? "—";
  const champion = player.champion ? championLabel(player.champion) : "";
  const damageShare = maxDamage > 0 ? (player.damageToChampions / maxDamage) * 100 : 0;

  return (
    <div className="flex items-center gap-2 border-b border-border/40 py-1.5 text-xs last:border-0">
      {/* Уровень углом портрета — как в табло LCU. Отдельной колонкой он не стоит
          своей ширины: разброс уровней в конце игры мал и строку не различает. */}
      <span className="relative h-7 w-7 shrink-0">
        <ChampionIcon
          name={player.champion ?? ""}
          label={champion}
          size={32}
          className="h-7 w-7 rounded object-cover"
          title={champion || undefined}
        />
        {player.level > 0 && (
          <span
            className="absolute -bottom-0.5 -right-0.5 rounded bg-background/90 px-0.5 text-[9px] font-semibold leading-tight tabular-nums text-muted-foreground"
            title={t("levelHint", { value: player.level })}
          >
            {player.level}
          </span>
        )}
      </span>

      {/* KAN-797: роль. Состав уже сортируется по ней на бэкенде, но без подписи
          порядок читался как случайный — а роль это первое, по чему ищут строку. */}
      <span className="w-8 shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
        {roleLabel(player.position)}
      </span>

      <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={name}>
        {name}
      </span>

      <span className="w-14 shrink-0 text-right tabular-nums">
        {player.kills}/{player.deaths}/{player.assists}
      </span>

      <span className="w-9 shrink-0 text-right tabular-nums text-muted-foreground">
        {int(player.cs)}
      </span>

      <span className="w-11 shrink-0 text-right tabular-nums text-amber-600/90">
        {short(player.gold)}
      </span>

      {/* Урон: число поверх собственной полоски. Отдельной колонкой полоска съедала
          место у имени игрока, а имя важнее — по нему строку и находят. Шкала общая
          на матч, поэтому длина сравнима между командами. */}
      <span
        className="relative w-14 shrink-0 overflow-hidden rounded text-right"
        title={t("damageTakenHint", { value: int(player.damageTaken) })}
      >
        <span
          aria-hidden
          className={`absolute inset-y-0 right-0 rounded ${
            side === "blue" ? "bg-blue-500/25" : "bg-red-500/25"
          }`}
          style={{ width: `${Math.max(4, Math.min(100, damageShare))}%` }}
        />
        <span className="relative px-1 tabular-nums">{short(player.damageToChampions)}</span>
      </span>

      <span
        className="w-7 shrink-0 text-right tabular-nums text-muted-foreground"
        title={t("wardsHint", { value: int(player.wardsPlaced) })}
      >
        {player.visionScore}
      </span>
    </div>
  );
}

/** Шапка колонок табло — иначе числа в строках читаются как набор без подписи. */
function ScoreboardHead() {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  return (
    <div className="flex items-center gap-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
      <span className="w-7 shrink-0" />
      <span className="w-8 shrink-0" />
      <span className="min-w-0 flex-1" />
      <span className="w-14 shrink-0 text-right">{t("kda")}</span>
      <span className="w-9 shrink-0 text-right">{t("cs")}</span>
      <span className="w-11 shrink-0 text-right" title={t("gold")}>
        <Coins className="ml-auto h-3 w-3" />
      </span>
      <span className="w-14 shrink-0 text-right" title={t("damage")}>
        <Swords className="ml-auto h-3 w-3" />
      </span>
      <span className="w-7 shrink-0 text-right" title={t("vision")}>
        <Eye className="ml-auto h-3 w-3" />
      </span>
    </div>
  );
}

function TeamScoreboard({
  team,
  side,
  maxDamage,
}: {
  team: ReplayTeamMetrics | undefined;
  side: "blue" | "red";
  maxDamage: number;
}) {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  if (!team) return null;

  const kills = Math.round(team.totals.kills);
  const deaths = Math.round(team.totals.deaths);
  const assists = Math.round(team.totals.assists);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 py-1">
        <span className="text-sm font-semibold text-foreground">
          {team.teamName ?? t(side)}
          {team.wins > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{t("win")}</span>
          )}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {kills}/{deaths}/{assists} · {short(team.totals.gold)}
        </span>
      </div>

      <ScoreboardHead />
      {team.players.map((player, index) => (
        <PlayerRow
          key={player.userId ?? `${player.riotIdGameName}-${index}`}
          player={player}
          side={side}
          maxDamage={maxDamage}
        />
      ))}
    </div>
  );
}

/** Всё содержимое одной игры: объекты сверху, затем два табло. */
function GameBody({ blue, red }: GamePair) {
  const maxDamage = Math.max(
    1,
    ...[...(blue?.players ?? []), ...(red?.players ?? [])].map((p) => p.damageToChampions),
  );

  return (
    <div className="space-y-3">
      <ObjectivesStrip blue={blue} red={red} />
      <div className="grid gap-4 lg:grid-cols-2">
        <TeamScoreboard team={blue} side="blue" maxDamage={maxDamage} />
        <TeamScoreboard team={red} side="red" maxDamage={maxDamage} />
      </div>
    </div>
  );
}

/**
 * Строка списка игр: номер, стороны, счёт по килам и длительность. Достаточно, чтобы
 * выбрать нужную игру, не раскрывая все подряд; победившая сторона выделена.
 */
function GameSummary({ pair, index }: { pair: GamePair; index: number }) {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  const { blue, red } = pair;
  const duration = blue?.durationSeconds ?? red?.durationSeconds ?? null;

  return (
    <div className="flex flex-1 flex-wrap items-baseline gap-x-3 gap-y-1 pr-2 text-sm">
      <span className="font-medium">{t("game", { number: index + 1 })}</span>

      <span className="flex items-baseline gap-1.5 text-xs">
        <span
          className={
            (blue?.wins ?? 0) > 0 ? "font-semibold text-foreground" : "text-muted-foreground"
          }
        >
          {blue?.teamName ?? t("blue")}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {blue ? Math.round(blue.totals.kills) : "—"} : {red ? Math.round(red.totals.kills) : "—"}
        </span>
        <span
          className={(red?.wins ?? 0) > 0 ? "font-semibold text-foreground" : "text-muted-foreground"}
        >
          {red?.teamName ?? t("red")}
        </span>
      </span>

      {duration != null && (
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {clock(duration)}
        </span>
      )}
    </div>
  );
}

/** Каркас табло: заголовок и один блок игры — высота близка к настоящему. */
function MatchGameTeamStatsSkeleton() {
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-64" />
      </div>
      <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    </section>
  );
}

export function MatchGameTeamStats({ matchId }: MatchGameTeamStatsProps) {
  const t = useTranslations("Bracket.modals.matchTeamStats");
  const { data, isLoading } = useMatchTeamStats(matchId);
  // Тот же гейт, что у турнирной витрины: модалка открывается мгновенно, а табло
  // подгружается — без каркаса блок выпрыгивал и сдвигал содержимое под собой.
  const showSkeleton = useDelayedLoading(isLoading);

  if (showSkeleton) return <MatchGameTeamStatsSkeleton />;
  if (isLoading) return null;
  if (!data || data.length === 0) return null;

  // Бэкенд отдаёт по строке на сторону каждой игры; собираем пары по игре.
  const byGame = new Map<number, GamePair>();
  for (const row of data) {
    if (row.gameId == null) continue;
    const entry = byGame.get(row.gameId) ?? {};
    if (row.teamSide === "100") entry.blue = row;
    else if (row.teamSide === "200") entry.red = row;
    byGame.set(row.gameId, entry);
  }

  const games = [...byGame.entries()].sort((a, b) => a[0] - b[0]);
  if (games.length === 0) return null;

  const header = (
    <div>
      <h3 className="text-sm font-semibold text-foreground">{t("title")}</h3>
      <p className="text-xs text-muted-foreground">{t("subtitle")}</p>
    </div>
  );

  // Одна игра — списка не нужно: сворачивать нечего, лишний клик только мешает.
  if (games.length === 1) {
    const pair = games[0]![1];
    const duration = pair.blue?.durationSeconds ?? pair.red?.durationSeconds ?? null;
    return (
      <section className="space-y-3">
        {header}
        <div className="rounded-lg border border-border bg-muted/20 p-3">
          {duration != null && (
            <div className="mb-2 text-right text-xs tabular-nums text-muted-foreground">
              {clock(duration)}
            </div>
          )}
          <GameBody {...pair} />
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      {header}
      <Accordion
        type="single"
        collapsible
        defaultValue={`game-${games[0]![0]}`}
        className="rounded-lg border border-border bg-muted/20 px-3"
      >
        {games.map(([gameId, pair], index) => (
          <AccordionItem key={gameId} value={`game-${gameId}`}>
            <AccordionTrigger className="hover:no-underline">
              <GameSummary pair={pair} index={index} />
            </AccordionTrigger>
            <AccordionContent>
              <GameBody {...pair} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
